import Button from "@/components/Button/index.js";
import { assetUrl } from '@/utils/asset-url';
import { ToastContext } from "@/context/toast/index.js";
import useConfig from "@/hooks/use-config/index.js";
import useEclips from "@/hooks/use-eclips/index.js";
import useModal from "@/hooks/use-modal/index.js";
import { useNavigate, useParams } from "@/lib/router-dom";
import api from "@/services/api/eclips.js";
import API_FILES from "@/services/api/files.js";
import { transformCSVtoJSON } from "@/utils/csvtojson";
import addFormats from "@/utils/custom-ajv-formats";
import Ajv from "ajv";
import { Fragment, useContext, useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { useSelector } from 'react-redux';
import AddDataElementModal from "./components/add-data-element-modal.jsx";
import AddReferenceNumberModal from "./components/add-reference-number-modal.jsx";
import ChangeReceipientModal from "./components/change-receipient-modal.jsx";
import ConfirmSendModal from "./components/confirm-send-modal.jsx";
import DownloadDataModal from "./components/download-data-modal.jsx";
import ActivityLog from "./components/eclip-audit-trail.jsx";
import EclipProgress from "./components/eclip-progress.jsx";
import { TitleHeader, TitleSubHeader } from "./components/header.jsx";
import ImportDataModal from "./components/import-data-modal.jsx";
import PackageOwnerSwitch from "./components/package-owner-switch.jsx";
import PackageTagCard from "./components/package-tag-card.jsx";
import ReceipientSwitch from "./components/receipient-switch.jsx";
import ReceivingDataElementCard from "./components/receiving-data-element-card.jsx";
import RequestMoreInformationModal from "./components/request-more-information-modal.jsx";
import SendingDataElementCard from "./components/sending-data-element.jsx";
import UpdatereferenceNumberModal from "./components/update-reference-number-modal.jsx";
import generateFileTypeErrorMessage from "@/utils/generate-file-type-error-message.js";

const ajv = new Ajv({ allErrors: true });
addFormats(ajv);

export function CustomPackageRecipientMessage(selectedReceiver, isCustom) {
	return (!selectedReceiver && isCustom && <div className="eclip-create__more-warning">
		<div>
			<span className="error">Note:</span> Select Recipient in order
			to add Data Elements
		</div>
	</div>)
}

export function RemovedMessage(isRemoved, isLoading = false) {
	return (isRemoved && !isLoading &&
		<div className="eclip-create__error-message">
			<span>Error:</span> Data element access has been revoked.
		</div>
	);
}

export function DeclinedMessage(isDeclined) {
	return (isDeclined &&
		<div className="eclip-create__more-warning">
			<div>
				<span className="error">Note:</span> The package has been
				declined.
			</div>
		</div>
	);
}

export function RemovedInfoMessage(isRemoved) {
	return (isRemoved &&
		<div className="eclip-create__empty-data-elements">
			<div className="eclip-data-elements__title">
				<span>{"Data Elements"}</span>
			</div>
			<div className="eclip-create__info-message">
				Data elements and attachments are no longer visible or
				accessible.
			</div>
		</div>
	);
}

export function ReceivedMessage(data) {
	return (data?.receivedMessage?.length > 0 &&
		<div className="eclip-create__more-info">
			<span className="eclip-create__more-info__title">
				Request For More Information
			</span>
			<span className="eclip-create__more-info__message">
				{data?.receivedMessage}
			</span>
		</div>
	)
}

const Page = () => {

	/*=================================================
		General
	/================================================*/
	const { provideItems } = useConfig();
	const { eclipId } = useParams();
	const { t } = useTranslation();
	const { addToastMessage } = useContext(ToastContext);
	const { goTo } = useNavigate();
	const eclipTemplates = useSelector(state => state.staticData.data.eclipTemplates)

	const {
		update,
		get,
		loading,
		send,
		currentItem: data,
		requestMoreInfo,
		setLoading,
		setRefNumber,
		getDataElements,
		dataElements: targetDataElements,
		setDataElements: setTargetDataElements,
		currentDataElement,
		setCurrentDataElement,
		packageOwnerUpdate,
		addRemoveDataElements,
		getActivityLog,
		activity
	} = useEclips();
	const isCustom = data?.templateId === "custom-template";

	const hasDraft = () => {
		const draftDataElements = dataElements?.filter(item => item?.isDraft === true);
		return draftDataElements.length > 0 ? true : false;
	}

	/*=================================================
		API
	/================================================*/

	const handleConfirmSend = () => {
		setIsConfirmSendVisible(false);
		send(eclipId, {
			recipients: recipients?.filter((r) => r.selected)?.map(({ on_behalf_of, ...rest }) => {
				return rest;
			}),
			dataElements: dataElements
				?.filter(
					(item) =>
						(data?.type === "SEND" && item?.type === "SEND") ||
						(item?.type === "RECEIVER_SEND" &&
							data?.type === "RECEIVE")
				)
				?.map(
					({
						dataElementId,
						payload,
						sourceFilename,
						attachments,
						source,
						onBehalfOf
					}) => ({
						dataElementId,
						attachments,
						sourceFilename,
						payload: [payload],
						source,
						onBehalfOf,
						parent_node_id: eclipId,
						parent_node_type: 'PACKAGE',
					})
				),
		})
			.then(() => {
				addToastMessage("Package successfully sent", "success");
				return get(eclipId);
			})
			.catch((err) => {
				setLoading(false);
				addToastMessage(
					err.response?.data?.error?.message ||
					"Failed to send eclip",
					"error"
				);
			});
	};

	const handleChange = async () => {
		await update(eclipId, {
			recipients: recipients?.filter((r) => r.selected)?.map(({ on_behalf_of, ...rest }) => {
				return rest;
			}),
			dataElements: dataElements?.filter(
				(item) =>
					(data?.type === "SEND" && item?.type === "SEND") ||
					(item?.type === "RECEIVER_SEND" &&
						data?.type === "RECEIVE")
			)?.map(
				({
					dataElementId,
					payload,
					sourceFilename,
					attachments,
					source,
					onBehalfOf
				}) => {

					return ({
						dataElementId,
						attachments,
						sourceFilename,
						payload: [payload],
						source,
						onBehalfOf,
						parent_node_id: eclipId,
						parent_node_type: 'PACKAGE',
					})
				}
			),
		}, true);
		getActivityLog(eclipId);
	};

	const handleSubmitRequestInformation = async (message) => {
		hideRequestInformationModal();
		await requestMoreInfo(eclipId, {
			message,
		})
			.then(() => {
				addToastMessage("Request sent", "success");
				return get(eclipId);
			})
			.catch((err) => {
				setLoading(false);
				addToastMessage(
					err.response?.data?.error?.message ||
					"Failed to send request",
					"error"
				);
			});
	};

	const handleSubmitAddRefernce = async (referenceNumber) => {
		hideAddRefNumberModal();
		hideUpdateRefNumberModal();
		await setRefNumber(eclipId, {
			referenceNumber,
		})
			.then(() => {
				addToastMessage("Reference ID Updated", "success");
				return get(eclipId);
			})
			.catch((err) => {
				setLoading(false);
				addToastMessage(
					err.response?.data?.error?.message ||
					"Failed to update Reference ID",
					"error"
				);
			});
	};

	const handleUpdatePackage = () => {
		send(eclipId, {
			recipients: recipients?.filter((r) => r.selected)?.map(({ on_behalf_of, ...rest }) => {
				return rest;
			}),
			dataElements: dataElements
				?.filter(
					(item) =>
						data?.type === "SEND" && item?.type === "SEND" &&
						item?.isDraft === true
				)
				?.map(
					({
						dataElementId,
						payload,
						sourceFilename,
						attachments,
						source,
						onBehalfOf
					}) => ({
						dataElementId,
						attachments,
						sourceFilename,
						payload: [payload],
						source,
						isDraft: true,
						onBehalfOf
					})
				),
		})
			.then(() => {
				addToastMessage("Package successfully updated", "success");
				return get(eclipId);
			})
			.catch((err) => {
				setLoading(false);
				addToastMessage(
					err.response?.data?.error?.message ||
					"Failed to update eclip",
					"error"
				);
			});
	}

	/*=================================================
		Modal
	================================================*/
	const { visible: isReciepientChangeModalVisible, hide: hideReciepientChangeModal, show: showRecipientChangeModal } = useModal();
	const { visible: isRequestInformationModalVisible, hide: hideRequestInformationModal, show: showRequestInformationModal } = useModal();
	const { visible: isAddRefNumberModalVisible, hide: hideAddRefNumberModal, show: showAddRefNumberModal } = useModal();
	const { visible: isDownloadModalVisible, hide: hideDownloadModal, show: showDownloadModal } = useModal();
	const { visible: isAddDataElementModalVisible, hide: hideAddDataElementModal, show: showAddDataElementModal } = useModal();
	const { visible: isImportDataModalVisible, hide: hideImportDataModal, show: showImportDataModal } = useModal();
	const { visible: isUpdateRefNumberModalVisible, show: showUpdateRefNumberModal, hide: hideUpdateRefNumberModal } = useModal();

	const [isConfirmSendVisible, setIsConfirmSendVisible] = useState(false);
	const [dataElements, setDataElements] = useState([]);
	const [recipients, setRecipients] = useState([]);
	const [messageStoreId, setMessageStoreId] = useState([]);

	const [toBeUpdatedDataElementList, setToBeUpdatedDataElementList] = useState([])

	const handleSendClick = () => {
		if (recipients) {
			setIsConfirmSendVisible(true);
		}
	};

	const handleCancelSend = () => {
		setIsConfirmSendVisible(false);
	};

	const openRecipientChangeModal = () => {
		showRecipientChangeModal();
	};

	const closeRecipientChangeModal = async (value) => {
		hideReciepientChangeModal();
		if (!value) {
			return;
		}
		const existing = data?.recipients.find((item) => item?.selected);
		const newRecipients = recipients?.map((item) => {
			item.selected = item.id === value.id;
			return item;
		});
		if (isCustom) {
			const current = newRecipients.find((item) => item?.selected);
			if (value?.id !== existing?.id || !value) {
				await addRemoveDataElements(
					eclipId,
					"REMOVE",
					dataElements?.map((item) => ({
						dataElementId: item.dataElementId,
						type: item.type,
					}))
				);
				await update(eclipId, {
					recipients: newRecipients?.filter((r) => r.selected)?.map(({ on_behalf_of, ...rest }) => {
						return rest;
					}),
					dataElements: []
				}, true);
			}
		} else {
			await handleChange();
		}
		setRecipients(newRecipients);
	};

	const handleAddViaForm = (dataElementId, type) => {
		const dataElement = dataElements.find(s => s.dataElementId === dataElementId && s.type === type);

		const readOnly = ((data?.type === "SEND" && dataElement?.type === "RECEIVER_SEND") ||
			(data?.type === "RECEIVE" && dataElement?.type === "SEND") ||
			(data?.sent && !dataElement.isDraft) ||
			dataElement?.savedPayloadSource === "IMPORTED") ? true : false

		goTo(
			`/eclip/update/${eclipId}/${dataElementId}/${type}`,
			{
				dataElement,
				isReadOnly: readOnly ,
				version: 'v1',
				recipients: [data.recipient]
			}
		);
	};

	const handleAddAttachment = (dataElementId) => {
		document.getElementById(dataElementId).value = "";
		document.getElementById(dataElementId).click();
	};

	const parseCsv = async (file, dataElementId) => {
		const dataElement = dataElements.find(
			(item) => item.dataElementId === dataElementId
		);
		return new Promise((resolve, reject) => {
			const fileReader = new FileReader();
			fileReader.readAsText(file, "UTF-8");
			fileReader.onload = async (e) => {
				let json;

				if (file.type === "application/json") {
					json = JSON.parse(e.target.result);
				}

				if (file.type === "text/csv" || file.type === "application/vnd.ms-excel") {

					const { convertedJSON } = await transformCSVtoJSON(e.target.result, dataElement?.elementSchema);

					//oneOf or anyOf situation need to use the correct schema to formatJSON
					const validate = ajv.compile(dataElement?.elementSchema);

					const valid = validate({
						...convertedJSON[0]
					});

					if (!valid) {
						reject(
							`Invalid data uploaded for ${dataElement?.elementName} - ${validate.errors?.[0]?.message}`
						);
					} else {
						let checkJSON = [{ ...convertedJSON[0] }]

						if (convertedJSON[0]?.attachments) {
							checkJSON = [{
								...convertedJSON[0],
								attachments: []
							}]
						}

						json = checkJSON
					}
					resolve(json);
					return;
				}
				reject(new Error("Invalid data"));
			};
		});
	};

	const handleDataUpload = async (file, dataElementId, type) => {
		if (!file) return;
		if (file.type !== "text/csv") {
			addToastMessage(generateFileTypeErrorMessage([".csv"]), "error");
			return
		};

		const result = await parseCsv(file, dataElementId).catch((message) => {
			addToastMessage(message, "error");
			return [];
		});

		if (!result || !result.length) {
			return;
		}

		const newDataElements = dataElements.map((item) => {
			if (item.dataElementId === dataElementId && item.type === type) {
				let payload = {
					...result[0]
				};// take first element from the array

				if (item?.payload?.attachments?.length) {
					payload = {
						...payload,
						attachments: []
					}
				}

				item.payload = payload
				item.sourceFilename = file.name;
				item.source = "CSV";
			}
			return item;
		});

		handleChange();

		setDataElements(newDataElements);
	};

	const handleStructuredDataRemove = (dataElementId, type) => {
		const newDataElements = dataElements.map((item) => {
			if (item.dataElementId === dataElementId && item.type === type) {
				item.payload = {}
				item.sourceFilename = "";
			}
			return item;
		});
		handleChange();
		setDataElements(newDataElements);
	};

	const handleRemoveAttachment = (dataElementId, type, index) => {
		const newDataElements = dataElements.map((item) => {
			if (item.dataElementId === dataElementId && item.type === type) {
				item.payload.attachments = item.payload?.attachments?.filter(
					(item, i) => i !== index
				);
			}

			if (item?.payload?.attachments?.length === 0) {
				delete item.payload.attachments
			}

			return item;
		});
		handleChange();
		setDataElements(newDataElements);
	};

	const handleFileUpload = async (file, dataElementId, type) => {
		if (!file) {
			return;
		}
		let sameFilename = dataElements.find(s => s.dataElementId == dataElementId && s.type == type)?.payload?.attachments?.find(s => s.filename == file.name)
		if (sameFilename) {
			addToastMessage("Same filename uploaded", "error");
			return;
		}
		setLoading(true);
		const [error, uploadFiles] = await API_FILES.upload([file]);
		setLoading(false);

		if (error) {
			addToastMessage(
				error.error?.message || "File upload failed",
				"error"
			);
			return;
		}
		let uploadFile = uploadFiles[0]?.data?.data?.[0];

		const newDataElements = dataElements.map((item) => {
			if (item.dataElementId === dataElementId && item.type === type) {
				if (!item.payload) {
					item.payload = {};
				}
				if (!item.payload.attachments) {
					item.payload.attachments = [];
				}
				item.payload?.attachments.unshift({
					filename: uploadFile.filename,
					file_content: uploadFile.file_content,
				});
			}
			return item;
		});
		handleChange();
		setDataElements(newDataElements);
	};

	const handleImportDataClick = async (dataElementId, type) => {
		const dataElement = dataElements.find(
			(item) => item.dataElementId === dataElementId
		);
		setCurrentDataElement(dataElement);
		showImportDataModal();
	};

	const handleUpdateIsNew = async (dataElementId, isNew, type, eclipType) => {
		if (!isNew)
			return false;

		const [error] = await api.updateIsNew(eclipId, dataElementId);

		if (error) {
			addToastMessage(error || "Failed to update new data element", "error");
			return false
		}

		return true
	};

	/*=================================================
		Set Contributor Lists and DE update
	================================================*/

	// Set Contributor Value for each DE
	const setContributor = async (dataElementId, value, userSelect) => {
		value.$userSelected = userSelect;
		dataElements?.forEach((item) => {
			if (item.dataElementId === dataElementId) {
				item.onBehalfOf = [value];
				return item;
			}
		});
		handleChange();
	}

	// Set Contributor Lists for each DE
	function setProvideItemsList(item) {
		let providerLists = [];
		provideItems?.filter((d) => {
			if (d.id === item.dataElementId && item.isOnBehalfOf) {
				d.to.filter((value) => value.id === selectedReceiver.id)?.forEach((element) => {
					element.on_behalf_of.forEach((innerElement) => {
						const found = providerLists.some(el => el.id === innerElement.id);
						if (!found) providerLists.push(innerElement)
					});
				});
			}
		})
		return providerLists;
	}

	function checkList(item, recipient) {
		const found = provideItems?.find((dataElement) => dataElement.id === item.dataElementId)
		const onBehalfOf = found?.to.find((orgId) => orgId.id === recipient)?.on_behalf_of

		if (onBehalfOf?.length > 1 || !recipient) {
			return null
		}

		const onBehalfOfObject = {
			...onBehalfOf?.[0],
			$userSelected: false
		}

		// setContributor(item.dataElementId,onBehalfOf[0],false)
		return {
			dataElementId: item.dataElementId,
			onBehalfOf: onBehalfOfObject,
		}
	}

	/*=================================================
		Load
	================================================*/

	const selectedReceiver = recipients?.find((item) => item?.selected) || data?.recipient;

	const isValidForm = selectedReceiver?.id?.length > 0;

	const isRemoved = dataElements?.length !== data?.templateDataElementsCount;
	const isDeclined = data?.badge === "PACKAGE_DECLINED";

	const isEditable = !data?.sent && !isDeclined;

	const isCopy = data?.receivingType === 'COPY'

	const [isDataElementsAvailableToSend, setIsDataElementsAvailableToSend] = useState(false);

	const checkDataElementsAvailableToSend = () => {
		// console.log('dataElements', dataElements)
		const dataElementsToSend = dataElements?.filter(
			(dataElement) =>
				dataElement?.payload &&
				Object.keys(dataElement?.payload).length > 1
		);

		setIsDataElementsAvailableToSend(dataElementsToSend?.length > 0);
	};

	useEffect(checkDataElementsAvailableToSend, [dataElements]);

	const init = async () => {
		   await get(eclipId);
		   await loadDataElements();
		   await getActivityLog(eclipId);
	};

	const loadDataElements = async () => {
		if (selectedReceiver?.id?.length > 0) {
			await getDataElements({
				org_id: selectedReceiver?.id,
				eclip_id: eclipId,
			});
		} else {
			setTargetDataElements([]);
		}
	};
	useEffect(() => { loadDataElements() }, [selectedReceiver?.id, eclipId]);

	useEffect(() => {
		console.log('runs on mount')
		api.createActivityLog(eclipId, {
			type: "view",
		});
		init()
	}, []);

	useEffect(() => {
		const dataElementListTobeUpdated = []
		const load = async () => {
			if (data) {
				const newDataElements = await Promise.all(data.dataElements
					// ?.filter((item) => item?.type === 'SEND')
					?.map(async (item) => {
						if (
							(
								(data?.type === "SEND" && item?.type === "SEND") ||
								(item?.type === "RECEIVER_SEND" && data?.type === "RECEIVE")
							) && !item.onBehalfOf
						) {
							dataElementListTobeUpdated.push(checkList(item, data?.recipient?.id))
						}

						const schema = item.elementSchema;
						if (item?.dataElementId === "other_documents") {
							schema.required = [
								...schema.required,
								"attachments",
							];
						}
						// Removing minimum length validation for cases where attachments is not a required field
						// Bug from Element Schema of the data elements where minItems value is 1 on not required field
						if (schema?.properties?.attachments && !schema?.required?.includes("attachments")) {
							schema.properties.attachments.minItems = 0;
						}

						let isValidPayload = true;
						let isAttachmentsAdded = item?.savedPayload?.[0]?.attachments?.length > 0;

						const isPayloadAdded = item?.savedPayload?.length > 0 && Object.keys(item?.savedPayload[0]).length > 1;

						if (item.savedPayloadSource === "IMPORTED") {
							isValidPayload = true;
						} else if (isPayloadAdded) {
							const validate = ajv.compile(schema);
							isValidPayload = validate({
								...item.savedPayload[0]
							});
						} else if (!isPayloadAdded && item?.required) {
							isValidPayload = false;
						}

						if (isAttachmentsAdded && !isPayloadAdded) {
							isValidPayload = false;
						}

						let isDownloadable = false;
						if (isValidPayload || isAttachmentsAdded || isPayloadAdded) {
							isDownloadable = (data?.type === "RECEIVE" && item?.type === "SEND") || (data?.type === "SEND" && item?.type === "RECEIVER_SEND");
						}

						if (item?.messageStoreId === null || isDeclined) {
							isDownloadable = false;
						}

						return {
							...item,
							sourceFilename: item.savedPayloadFilename || "",
							payload: item.savedPayload[0], // take first element from the array
							source: item.savedPayloadSource || "EFORM",
							isDownloadable,
							isValidPayload,
							isAttachmentsAdded,
							isPayloadAdded,
							parent_node_id: eclipId,
							parent_node_type: 'PACKAGE',
						};
					})
				);
				setDataElements(newDataElements);
				setMessageStoreId(
					newDataElements
						?.filter((item) => item?.isDownloadable && !item?.hasExpired)
						?.map((item) => item?.messageStoreId)
				);
				setRecipients(data.recipients);
			}
		};
		load();
		setToBeUpdatedDataElementList(dataElementListTobeUpdated)
	}, [data]);

	useEffect(async () => {
		if (toBeUpdatedDataElementList.length > 0 && recipients.length > 0 && dataElements.length > 0) {
			const recipient = recipients.filter((r) => r.selected)?.map(({ on_behalf_of, ...rest }) => {
				return rest;
			})

			const dataElementsList = dataElements?.filter(
				(item) =>
					(data?.type === "SEND" && item?.type === "SEND") ||
					(item?.type === "RECEIVER_SEND" && data?.type === "RECEIVE")
			)?.map(({
				dataElementId,
				payload,
				sourceFilename,
				attachments,
				source,
				onBehalfOf
			}) => {
				const findIndex = toBeUpdatedDataElementList.findIndex((dataElement) => dataElementId === dataElement?.dataElementId)
				return {
					dataElementId,
					attachments,
					sourceFilename,
					payload: [payload],
					source,
					onBehalfOf: findIndex < 0 ? onBehalfOf : [toBeUpdatedDataElementList[findIndex]?.onBehalfOf],
					parent_node_id: eclipId,
					parent_node_type: 'PACKAGE',
				}
			})

			const payload = {
				recipients: recipient,
				dataElements: dataElementsList
			}

			if (!toBeUpdatedDataElementList.every((item) => item === null)) {
				const error = await update(eclipId, payload, false)
				if (error) {
					return
				}

				await get(eclipId);
			}
		}
	}, [toBeUpdatedDataElementList, recipients, dataElements])

	function checkEclipTemplate() {
		let templateName = "custom-template"
		if (!isCustom && eclipTemplates) {
			const template = eclipTemplates?.find((template) => template.id === data?.templateId)
			templateName = template?.name
		}

		return templateName
	}

	useEffect(() => {
		const loadContributorLists = async () => {
			if (dataElements) {
				const newDataElements = dataElements?.map((item) => {
					if (item.isOnBehalfOf && !item.onBehalfOf) {
						let list = setProvideItemsList(item);
						list.forEach(object => {
							object.$userSelected = false;
						});
						item.onBehalfOf = list.length === 1 ? list : item.onBehalfOf;
					}
					return {
						...item,
					};
				});
			}
		};
		loadContributorLists();
	}, []);

	const handleRequestInformation = () => {
		showRequestInformationModal();
	};

	const handleAddReference = () => {
		showAddRefNumberModal();
	};

	const handleUpdateReference = () => {
		showUpdateRefNumberModal();
	};

	const downloadButton = messageStoreId?.length > 0 ? (
		<div className="eclip-receipien__change">
			<DownloadDataModal
				visible={isDownloadModalVisible}
				close={hideDownloadModal}
				messageStoreId={messageStoreId}
				all={true}
				dataElementId={dataElements?.filter((item) => item?.isDownloadable && item.messageStoreId)?.map(s => s.dataElementId)}
				packageName={data?.name}
			/>
			<Button
				text={t("Download All")}
				sizing="Cancel"
				full
				ghost
				submit={() => {
					showDownloadModal();
				}}
			/>
		</div>
	) : (
		<></>
	);

	const addDataElement = isCustom && data?.type === "SEND" && !data?.receivedMessage ? (
		<div className="eclip-receipien__change">
			{targetDataElements?.length > 0 && (
				<AddDataElementModal
					visible={isAddDataElementModalVisible}
					close={hideAddDataElementModal}
					receiver={selectedReceiver}
					dataElements={targetDataElements}
					submit={init}
				/>
			)}
			<Button
				text={t("+ Add Data Element")}
				full
				ghost
				disabled={!targetDataElements?.length > 0}
				submit={() => {
					showAddDataElementModal();
				}}
			/>
		</div>
	) : (
		<></>
	);

	if (data?.type === "RECEIVE") {
		const isAttachmentsValid = dataElements?.every((item) => {
			if (item.type !== "RECEIVER_SEND") {
				return true;
			}
			return item.isValidPayload;
		});

		const requireReturn =
			data?.dataElements?.filter((item) => item?.type === "RECEIVER_SEND")
				?.length > 0;
		const enableRequestInfo = data?.dataElements?.length > 0;
		return (
			<>
				<div className="eclip-create__content">
					<TitleHeader
						{...data}
						loading={loading}
						setLoading={setLoading}
						enabled={
							isAttachmentsValid && isDataElementsAvailableToSend
						}
						handleRequestInformation={
							enableRequestInfo && handleRequestInformation
						}
						handleSendClick={
							requireReturn && isEditable && handleSendClick
						}
						handleAddReference={handleAddReference}
						handleUpdateReference={handleUpdateReference}
						isRemoved={isRemoved}
						reload={init}
						isCustom={isCustom}
						isCopy={isCopy}
						hasDraft={hasDraft}
					/>
					<TitleSubHeader
						template={checkEclipTemplate()}
						{...data}
					/>
					<div className="eclip-create-upload__content">
						<div className="eclip-create-upload__content__left">
							{ReceivedMessage(data)}
							{CustomPackageRecipientMessage(selectedReceiver, isCustom)}
							{RemovedMessage(isRemoved, loading)}
							{DeclinedMessage(isDeclined)}
							<div className="eclip-data-elements">
								{isRemoved ? (
									RemovedInfoMessage(isRemoved)
								) : (
									<>
										<div className="eclip-data-elements__title">
											<span>
												{"Data Elements"}
											</span>
											{downloadButton}
											{addDataElement}
										</div>
										<div data-testid="eclip-data-elements__list" className="eclip-data-elements__list">
											{dataElements?.map((item, index) => {
												return (
													<Fragment key={index}>
														{item.type === "SEND" ? (
															item?.isPayloadAdded ||
																item?.isAttachmentsAdded ? (
																<SendingDataElementCard
																	{...item}
																	eclipType={
																		data.type
																	}
																	isDeclined={
																		isDeclined
																	}
																	isCustom={isCustom}
																	isNew={item?.isNew}
																	isDraft={item?.isDraft}
																	reload={init}
																	handleAddViaForm={
																		handleAddViaForm
																	}
																	handleImportData={
																		handleImportDataClick
																	}
																	handleUpdateIsNew={
																		handleUpdateIsNew
																	}
																	sent={
																		data?.sent
																	}
																	setContributor={
																		setContributor
																	}
																	isOnBehalfOf={
																		item.isOnBehalfOf
																	}
																	provideItemsLists={
																		setProvideItemsList(item)
																	}
																	selectedContributor={
																		item?.onBehalfOf
																	}
																	currentPackage={data}
																/>
															) : (
																<ReceivingDataElementCard
																	isCustom={isCustom}
																	isEditable={
																		item?.isDraft
																	}
																	isDeclined={
																		isDeclined
																	}
																	reload={init}
																	handleImportData={
																		handleImportDataClick
																	}
																	{...item}
																	eclipType={
																		data.type
																	}
																	isCopy={isCopy}
																/>
															)
														) : (
															<SendingDataElementCard
																isCustom={isCustom}
																isDeclined={isDeclined}
																reload={init}
																{...item}
																eclipType={data.type}
																isEditable={isEditable || item?.isDraft}
																isNew={item?.isNew}
																isDraft={item?.isDraft}
																handleImportData={
																	handleImportDataClick
																}
																handleStructuredDataRemove={
																	handleStructuredDataRemove
																}
																handleDataUpload={
																	handleDataUpload
																}
																handleAddViaForm={
																	handleAddViaForm
																}
																handleAddAttachment={
																	handleAddAttachment
																}
																handleFileUpload={
																	handleFileUpload
																}
																handleRemoveAttachment={
																	handleRemoveAttachment
																}
																handleUpdateIsNew={
																	handleUpdateIsNew
																}
																sent={
																	data?.sent
																}
																isOnBehalfOf={
																	item.isOnBehalfOf
																}
																provideItemsLists={
																	setProvideItemsList(item)
																}
																selectedContributor={
																	item?.onBehalfOf
																}
																currentPackage={data}
															/>
														)}
													</Fragment>
												)
											})}
										</div>
									</>
								)}
							</div>
						</div>
						<div className="eclip-create-upload__content__right">
							<EclipProgress
								title={t("pecentageOfCompletion")}
								value={data?.completionPercentage}
							/>
							<ReceipientSwitch
								title={t("Sent by")}
								selected={selectedReceiver}
								imageUrl={`url(${selectedReceiver?.iconUrl})`}
							/>
							<PackageOwnerSwitch
								title={t("packageOwner")}
								submit={init}
								isRemoved={isRemoved}
								isDeclined={isDeclined}
								{...data}
								isCopy={isCopy}
							/>
							<PackageTagCard isEditable={isEditable} data={data?.tags} isCopy={isCopy} />
							<ActivityLog
								title={t("activityLog")}
								eclip={data}
								activity={activity}
							/>
						</div>
					</div>
				</div>
				<AddReferenceNumberModal
					visible={isAddRefNumberModalVisible}
					submit={handleSubmitAddRefernce}
					close={hideAddRefNumberModal}
				/>
				<RequestMoreInformationModal
					visible={isRequestInformationModalVisible}
					submit={handleSubmitRequestInformation}
					close={hideRequestInformationModal}
				/>
				<ConfirmSendModal
					isVisible={isConfirmSendVisible}
					title={`Send Package Confirmation`}
					text={`Are you sure want to send ${data?.name} to ${selectedReceiver?.name} ? Kindly confirm.`}
					cancel={handleCancelSend}
					confirm={handleConfirmSend}
				/>
				<ImportDataModal
					visible={isImportDataModalVisible}
					close={hideImportDataModal}
					submit={init}
					dataElement={currentDataElement}
				/>
				{isUpdateRefNumberModalVisible && (
					<UpdatereferenceNumberModal
						visible
						close={hideUpdateRefNumberModal}
						id={data?.referenceNumber}
						submit={handleSubmitAddRefernce}
					/>
				)}

				{loading &&
					ReactDOM.createPortal(
						<div className="modal white">
							<div className="loading">
								<img src={assetUrl("/loading.gif")} />
							</div>
						</div>,
						document.body
					)}
			</>
		);
	}

	const isAttachmentsValid = dataElements?.every((item) => {
		if (item?.type !== "SEND") {
			return true;
		}
		return item.isValidPayload;
	});

	const enableRequestMore = data?.receivedMessage?.length > 0 || data?.sent;

	return (
		<>
			<div className="eclip-create__content">
				<TitleHeader
					{...data}
					loading={loading}
					setLoading={setLoading}
					enabled={
						isValidForm &&
						isAttachmentsValid &&
						isDataElementsAvailableToSend
					}
					handleRequestInformation={
						enableRequestMore && handleRequestInformation
					}
					handleSendClick={isEditable && handleSendClick}
					handleAddReference={handleAddReference}
					handleUpdateReference={showUpdateRefNumberModal}
					handleUpdatePackage={handleUpdatePackage}
					isRemoved={isRemoved}
					reload={init}
					isCustom={isCustom}
					hasDraft={hasDraft}
				/>
				<TitleSubHeader
					template={checkEclipTemplate()}
					{...data}
				/>
				<div className="eclip-create-upload__content">
					<div data-testid="content__left" className="eclip-create-upload__content__left">
						{ReceivedMessage(data)}
						{RemovedMessage(isRemoved, loading)}
						{DeclinedMessage(isDeclined)}
						{CustomPackageRecipientMessage(selectedReceiver, isCustom)}
						<div className="eclip-data-elements">
							{isRemoved ? (
								RemovedInfoMessage(isRemoved)
							) : (
								<>
									{" "}
									<div className="eclip-data-elements__title">
										<span>{"Data Elements"}</span>
										<div className="flex-inline">
											{downloadButton}
											{addDataElement}
										</div>
									</div>
									<div data-testid="eclip-data-elements__list" className="eclip-data-elements__list">
										{dataElements?.map((item, index) =>
											item?.type === "SEND" ? (
												<SendingDataElementCard
													key={index}
													{...item}
													eclipType={data.type}
													isDeclined={isDeclined}
													isCustom={isCustom}
													isEditable={isEditable || item?.isDraft}
													isNew={item?.isNew}
													isDraft={item?.isDraft}
													hasExpired={item?.hasExpired}
													reload={init}
													handleStructuredDataRemove={
														handleStructuredDataRemove
													}
													handleDataUpload={
														handleDataUpload
													}
													handleAddViaForm={
														handleAddViaForm
													}
													handleAddAttachment={
														handleAddAttachment
													}
													handleFileUpload={
														handleFileUpload
													}
													handleRemoveAttachment={
														handleRemoveAttachment
													}
													handleImportData={
														handleImportDataClick
													}
													handleUpdateIsNew={
														handleUpdateIsNew
													}
													sent={
														data?.sent
													}
													setContributor={
														setContributor
													}
													isOnBehalfOf={
														item.isOnBehalfOf
													}
													provideItemsLists={
														setProvideItemsList(item)
													}
													selectedContributor={
														item?.onBehalfOf
													}
													currentPackage={data}
												/>
											) : item?.isPayloadAdded ||
												item?.isAttachmentsAdded ? (
												<SendingDataElementCard
													key={index}
													isCustom={isCustom}
													isDeclined={isDeclined}
													isNew={item?.isNew}
													isEditable={isEditable || item?.isDraft}
													isDraft={item?.isDraft}
													{...item}
													eclipType={data.type}
													handleAddViaForm={
														handleAddViaForm
													}
													handleImportData={
														handleImportDataClick
													}
													reload={init}
													sent={
														data?.sent
													}
													handleUpdateIsNew={
														handleUpdateIsNew
													}
													setContributor={
														setContributor
													}
													isOnBehalfOf={
														item.isOnBehalfOf
													}
													handleAddAttachment={
														handleAddAttachment
													}
													provideItemsLists={
														setProvideItemsList(item)
													}
													selectedContributor={
														item?.onBehalfOf
													}
													currentPackage={data}
												/>
											) : (
												<ReceivingDataElementCard
													key={`de-${index}`}
													isDeclined={isDeclined}
													{...item}
													eclipType={data.type}
													isCustom={isCustom}
													isEditable={isEditable || item?.isDraft}
													reload={init}
													handleUpdateIsNew={
														handleUpdateIsNew
													}
													isCopy={isCopy}
												/>
											)
										)}
									</div>
								</>
							)}
						</div>
					</div>
					<div className="eclip-create-upload__content__right">
						<EclipProgress
							title={t("pecentageOfCompletion")}
							value={data?.completionPercentage}
						/>
						<ReceipientSwitch
							title={t("intendedReceipient")}
							selected={selectedReceiver}
							imageUrl={`url(${selectedReceiver?.iconUrl})`}
							onClick={
								isEditable &&
								!data?.receivedMessage &&
								openRecipientChangeModal
							}
						/>
						<PackageOwnerSwitch
							title={t("packageOwner")}
							submit={init}
							isRemoved={isRemoved}
							isDeclined={isDeclined}
							{...data}
							isCopy={isCopy}
						/>
						<PackageTagCard isEditable={isEditable} data={data?.tags} isCopy={isCopy} />
						<ActivityLog title={t("activityLog")} eclip={data}
								activity={activity}/>
					</div>
				</div>
			</div>
			<AddReferenceNumberModal
				visible={isAddRefNumberModalVisible}
				submit={handleSubmitAddRefernce}
				close={hideAddRefNumberModal}
			/>
			<ImportDataModal
				visible={isImportDataModalVisible}
				close={hideImportDataModal}
				submit={init}
				dataElement={currentDataElement}
			/>
			<RequestMoreInformationModal
				visible={isRequestInformationModalVisible}
				submit={handleSubmitRequestInformation}
				close={hideRequestInformationModal}
			/>
			<ChangeReceipientModal
				showConfirm={isCustom && Boolean(selectedReceiver)}
				selected={selectedReceiver}
				isVisible={isReciepientChangeModalVisible}
				close={closeRecipientChangeModal}
				data={recipients}
			/>
			<ConfirmSendModal
				isVisible={isConfirmSendVisible}
				title={`Send Package Confirmation`}
				text={`Are you sure want to send ${data?.name} to ${selectedReceiver?.name} ? Kindly confirm.`}
				cancel={handleCancelSend}
				confirm={handleConfirmSend}
			/>
			{isUpdateRefNumberModalVisible && (
				<UpdatereferenceNumberModal
					visible
					close={hideUpdateRefNumberModal}
					id={data?.referenceNumber}
					submit={handleSubmitAddRefernce}
				/>
			)}

			{loading &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/loading.gif")} />
						</div>
					</div>,
					document.body
				)}
		</>
	);
};

export default Page;
